import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  Paper,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useGetMedicines } from "hooks/useMedicine";
import { MedicineResponseDto } from "libs/cellera-sdk";
import { useCallback, useMemo, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface MedicineSearchPanelProps {
  onSelectMedicine: (medicine: MedicineResponseDto) => void;
}

const formatActiveIngredients = (medicine: MedicineResponseDto) => {
  if (!medicine.activeIngredients || medicine.activeIngredients.length === 0) {
    return "N/A";
  }
  return medicine.activeIngredients
    .map((ing) => `${ing.name} ${ing.strength}`)
    .join("; ");
};

function MedicineSearchPanel({ onSelectMedicine }: MedicineSearchPanelProps) {
  const { colors } = useCelleraTheme();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 500);

  const { data: medicinesResponse, isLoading: isLoadingMedicines } =
    useGetMedicines({
      page: page,
      pageSize: rowsPerPage,
      search: debouncedSearchTerm,
    });

  const medicines = useMemo(() => {
    return medicinesResponse?.medicines || [];
  }, [medicinesResponse]);

  const pagination = useMemo(() => {
    return {
      total: medicinesResponse?.total || 0,
      page: medicinesResponse?.page || 1,
      pageSize: medicinesResponse?.pageSize || 25,
    };
  }, [medicinesResponse]);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage + 1); // MUI uses 0-based, API uses 1-based
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(1); // Reset to first page (1-based)
    },
    []
  );

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${colors.border.main}`,
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${colors.border.main}`,
          backgroundColor: colors.background.secondary,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          Search Medicines
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${colors.border.main}`,
        }}
      >
        <TextField
          fullWidth
          placeholder="Search by NDC, Medicine Name, or Description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.text.secondary }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: colors.background.paper,
            },
          }}
        />
      </Box>

      {/* Search Results */}
      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {isLoadingMedicines ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "400px",
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: colors.secondary.main }} />
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              Searching medicines...
            </Typography>
          </Box>
        ) : medicines.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "400px",
            }}
          >
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {searchTerm
                ? "No medicines found"
                : "Search for medicines to add"}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {medicines.map((medicine, index) => (
              <Box key={medicine.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => onSelectMedicine(medicine)}
                    sx={{
                      py: 2,
                      px: 2,
                      flexDirection: "column",
                      alignItems: "flex-start",
                      "&:hover": {
                        backgroundColor: colors.background.secondary,
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: colors.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {medicine.packageNdc && `[${medicine.packageNdc}] `}
                      {medicine.brandNameBase || medicine.name}
                      {medicine.description && `; ${medicine.description}`}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontSize: "0.75rem",
                      }}
                    >
                      {formatActiveIngredients(medicine)}
                    </Typography>
                  </ListItemButton>
                </ListItem>
                {index < medicines.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
      </Box>

      {/* Pagination */}
      {!isLoadingMedicines && medicines.length > 0 && (
        <Box
          sx={{
            borderTop: `1px solid ${colors.border.main}`,
            backgroundColor: colors.background.paper,
          }}
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page - 1} // Convert to 0-based for MUI
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) =>
              `Results: ${from}-${to} of ${count}`
            }
            sx={{
              border: "none",
              ".MuiTablePagination-toolbar": {
                paddingLeft: 2,
              },
              ".MuiTablePagination-displayedRows": {
                color: colors.text.secondary,
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
}

export default MedicineSearchPanel;
